import React, { useCallback, useEffect, useRef } from "react";
import "./styles/Navbar.css";
import { AuthSectionNames, DeveloperSectionNames, NavSectionNames, NavSections } from "../common/nav-sections";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../providers/AuthContext";
import { AppRoutes } from "../common/app-routes";
import { APP_NAME, LANDING_PAGE_CONTAINER_ID } from "../common/constants";
import { scrollToSectionWithOffset, waitForImagesToLoadThenScroll } from "../common/scrollUtils";
import NavDropdown from "./NavDropdown";
import { useOnGetBillingPortal } from "../common/paymentUtils";
import { useUsage } from "../providers/UsageProvider";
import { Plan } from "../services/UserService";

const sections = Object.values(NavSections);

interface NavbarProps {
  menuOpen: boolean;
  toggleMenu: () => void;
  setActiveSection: (val: string) => void;
  activeSection: string;
  changeActiveSection: (id: string) => void;
}

export const Navbar: React.FC<NavbarProps> = ({ menuOpen, toggleMenu, activeSection, changeActiveSection, setActiveSection }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const scrollTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { user, logout } = useAuth();
  const usage = useUsage();


  useEffect(() => {
    if (location.pathname !== AppRoutes.HOME) return;

    const handleScrollStop = () => {
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);

      scrollTimeout.current = setTimeout(() => {
        const midpoint = window.innerHeight / 2;
        let newActive: string | null = null;

        for (let id of sections) {
          const el = document.getElementById(id);
          if (el) {
            const rect = el.getBoundingClientRect();
            const isVisible = rect.top <= midpoint && rect.bottom >= midpoint;

            if (isVisible) {
              newActive = id;
              break;
            }
          }
        }

        if (newActive && newActive !== activeSection) {
          setActiveSection(newActive);
        }
      }, 75);
    };

    window.addEventListener("scroll", handleScrollStop, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScrollStop);
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    };
  }, [location.pathname, activeSection, setActiveSection]);

  const handleNavClick = useCallback((id: string) => {
    if (location.pathname === AppRoutes.HOME) {
      const el = document.getElementById(id);
      if (el) {
        scrollToSectionWithOffset(id);
      }
    } else {
      navigate(AppRoutes.HOME, { state: { skipHashScroll: true } });
      setTimeout(() => {
        waitForImagesToLoadThenScroll(id, LANDING_PAGE_CONTAINER_ID, 1000, 3);
      }, 150);
    }
  }, [location.pathname, navigate]);

  const onLogout = useCallback(() => {
    logout();
    navigate(AppRoutes.LOGIN);
    changeActiveSection(AuthSectionNames.LOGIN);
  }, [changeActiveSection, logout, navigate]);

  const onGetBillingUrlClick = useOnGetBillingPortal();

  return (
    <header className="navbar">
      <div className="navbar-container">
        <div className="navbar-left navbar-links">
          <div
            className="navbar-logo"
            onClick={() => {
              changeActiveSection(NavSections.WELCOME.toString());
              navigate("/");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            <span className="logo-with-title">
              <img src="/favicon-32x32.ico" alt="Cutora Logo" className="logo-img" width={32} height={32} />
              <span className="logo-text">{APP_NAME}</span>
            </span>
          </div>
        </div>

        <nav className={`navbar-center navbar-links ${menuOpen ? "open" : ""}`}>
          {sections.map((id) => (
            <a
              key={id}
              href={`#${id}`}
              className={activeSection === id ? "active" : ""}
              onClick={(e) => {
                e.preventDefault();
                changeActiveSection(id);
                handleNavClick(id);
                window.history.pushState(null, "", `#${id}`);
              }}
            >
              {NavSectionNames[id]}
            </a>
          ))}
          {user &&
            <Link
              to="/jobs"
              className={activeSection === "jobs" ? "active" : ""}
              onClick={() => {
                changeActiveSection("jobs");
              }}
            >
              Jobs
            </Link>}
          {user && usage.limits?.plan !== Plan.Free &&
            <a
              href={''}
              key={'Billing'}
              onClick={async (e) => {
                e.preventDefault();
                await onGetBillingUrlClick();
              }}
            >
              Billing
            </a>
          }

          {usage.limits?.plan === Plan.Developer &&
            <NavDropdown
              label="Developers"
              align="right"
              activeSection={activeSection}
              items={[
                { label: "Docs", href: `${AppRoutes.DOCS}` },
                { label: "Manage API", href: `${AppRoutes.MANAGE_API}` },
              ]}
              setActiveSection={() => changeActiveSection(DeveloperSectionNames.DEVELOPERS)}
            />
          }

        </nav>

        <div className={`navbar-right navbar-links ${menuOpen ? "open" : ""}`}>
          {!user ? (
            <>
              <Link
                to="/login"
                className={activeSection === "login" ? "active" : ""}
                onClick={() => {
                  changeActiveSection(AuthSectionNames.LOGIN);
                }}
              >
                Login
              </Link>
              <Link
                to="/register"
                className={activeSection === "register" ? "active" : ""}
                onClick={() => {
                  changeActiveSection(AuthSectionNames.REGISTER);
                }}
              >
                Register
              </Link>
            </>
          ) : (
            <div className="navbar-user-info">
              <div className="user-greeting">
                <span className="user-hello">Hello,</span>
                <span className="user-name">{user.name}</span>
              </div>
              <button
                onClick={onLogout}
                className="logout-icon-button"
                title="Logout"
                aria-label="Logout"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M16 13v-2H7V8l-5 4 5 4v-3h9zM20 3H9a2 2 0 00-2 2v4h2V5h11v14H9v-4H7v4a2 2 0 002 2h11a2 2 0 002-2V5a2 2 0 00-2-2z" />
                </svg>
              </button>
            </div>
          )}
        </div>
        <button
          className="navbar-toggle"
          onClick={toggleMenu}
        >
          ☰
        </button>
      </div>
    </header>
  );
};
